import { useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, KeyRound, Trash2, UserPlus } from "lucide-react";
import { api, useApi } from "../../lib/api";
import { formatApiError, useAuth } from "../../lib/auth";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const AddEditor = ({ onDone }) => {
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/users", f);
      toast.success(`Account created for ${f.email}`);
      setF({ name: "", email: "", password: "" });
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="border-2 border-ink bg-paper p-6 grid sm:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end" data-testid="team-add-form">
      <label className="block"><span className="kicker">Name</span><input required value={f.name} onChange={set("name")} className="admin-input mt-2" data-testid="team-add-name" /></label>
      <label className="block"><span className="kicker">Email</span><input required type="email" value={f.email} onChange={set("email")} className="admin-input mt-2" data-testid="team-add-email" /></label>
      <label className="block"><span className="kicker">Password (min 8)</span><input required minLength={8} value={f.password} onChange={set("password")} className="admin-input mt-2" data-testid="team-add-password" /></label>
      <button disabled={busy} className="inline-flex items-center justify-center gap-2 bg-hot text-ink border-2 border-ink kicker px-5 py-3.5 hover:bg-ink hover:text-cream transition-colors disabled:opacity-60" data-testid="team-add-submit">
        <UserPlus className="h-4 w-4" /> {busy ? "Adding…" : "Add"}
      </button>
    </form>
  );
};

const ResetPassword = ({ u, onClose }) => {
  const [pw, setPw] = useState("");
  const save = async () => {
    try {
      await api.put(`/admin/users/${u.id}/password`, { password: pw });
      toast.success(`New password set for ${u.email}`);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  return (
    <AlertDialog open onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent data-testid="team-reset-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>New password for {u.name}</AlertDialogTitle>
          <AlertDialogDescription>Share it with {u.email} privately. Minimum 8 characters.</AlertDialogDescription>
        </AlertDialogHeader>
        <input value={pw} onChange={(e) => setPw(e.target.value)} className="admin-input" data-testid="team-reset-input" />
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="team-reset-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction disabled={pw.length < 8} onClick={save} data-testid="team-reset-confirm">Save password</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default function AdminTeam() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data } = useApi("/admin/users");
  const users = Array.isArray(data) ? data : [];
  const [reset, setReset] = useState(null);
  const [del, setDel] = useState(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["/admin/users"] });

  const remove = async () => {
    try {
      await api.delete(`/admin/users/${del.id}`);
      toast.success("Account removed");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setDel(null);
  };

  if (user?.role !== "admin") {
    return <main className="pt-32 max-w-[900px] mx-auto px-5 kicker" data-testid="team-forbidden">Only the main admin can manage the team.</main>;
  }

  return (
    <main className="pt-28 pb-24 max-w-[1100px] mx-auto px-5 sm:px-10" data-testid="admin-team-page">
      <Link to="/admin" className="inline-flex items-center gap-2 kicker hover:text-rose" data-testid="team-back-link"><ArrowLeft className="h-4 w-4" /> All articles</Link>
      <h1 className="display text-6xl sm:text-7xl mt-4 border-b-2 border-ink pb-6">Team <span className="text-pink">{users.length}</span></h1>
      <p className="font-serif italic text-xl text-ink/75 mt-6 mb-6">Editors can write, schedule, edit and delete articles. Only you can add or remove people.</p>
      <AddEditor onDone={refresh} />
      <div className="mt-8">
        {users.map((u, i) => (
          <div key={u.id} className="flex flex-wrap items-center justify-between gap-4 py-4 border-b border-ink/15" data-testid={`team-row-${i}`}>
            <div>
              <p className="display text-2xl">{u.name || u.email}</p>
              <p className="kicker text-ink/60 mt-1">{u.email} · <span className={u.role === "admin" ? "text-rose" : ""}>{u.role === "admin" ? "main admin" : "editor"}</span></p>
            </div>
            {u.role === "editor" && (
              <div className="flex gap-2">
                <button onClick={() => setReset(u)} className="admin-icon-btn" aria-label="Reset password" data-testid={`team-reset-${i}`}><KeyRound className="h-4 w-4" /></button>
                <button onClick={() => setDel(u)} className="admin-icon-btn hover:!bg-rose" aria-label="Remove" data-testid={`team-delete-${i}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            )}
          </div>
        ))}
      </div>
      {reset && <ResetPassword u={reset} onClose={() => setReset(null)} />}
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent data-testid="team-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {del?.name}?</AlertDialogTitle>
            <AlertDialogDescription>{del?.email} won’t be able to sign in anymore. Their articles stay on the site.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="team-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="team-delete-confirm">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
